import { Parse } from 'parse';
import { mockLifeObjects } from './mock.fixtures';

const useMocks = () => process.env.REACT_APP_API_TRANSPORT !== 'parse';

export class LifeTaggerApiError extends Error {
  constructor(message, { code = 'request_failed', cause = null } = {}) {
    super(message);
    this.name = 'LifeTaggerApiError';
    this.code = code;
    this.cause = cause;
  }
}

const friendlyError = (error) => {
  if (error instanceof LifeTaggerApiError) return error;
  const code = error?.code || 'request_failed';
  const messages = {
    209: 'Your session has expired. Please sign in again.',
    100: 'LifeTagger could not be reached. Check your connection and try again.',
    101: 'That LifeObject is no longer available.',
  };
  return new LifeTaggerApiError(
    messages[code] ||
      error?.message ||
      'LifeTagger could not complete the request.',
    { code, cause: error }
  );
};

const normalize = (value) => {
  if (!value) return value;
  if (typeof value.toJSON === 'function') return value.toJSON();
  return value;
};

const contentTitle = (content = []) => {
  const body = content.find((item) => item.body)?.body;
  if (!body) {
    const mediaType = content.find((item) => item.type !== 'text')?.type;
    return mediaType
      ? `${mediaType === 'url-embed' ? 'Web' : mediaType} experience`
      : 'Untitled LifeObject';
  }
  const text = String(body)
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(
      /(?:^|\s)[.#]?[a-z_-][\w-]*(?:\s+[.#]?[a-z_-][\w-]*)*\s*\{[^{}]*\}/gi,
      ' '
    )
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text ? text.slice(0, 80) : 'HTML experience';
};

export const normalizeLifeObject = (value) => {
  const item = normalize(value);
  if (!item) return item;
  const legacyAnchors = [];
  (item.beaconId || []).forEach((identifier, index) =>
    legacyAnchors.push({
      id: `legacy-beacon-${index}-${identifier}`,
      name: 'LifeCode or nearby device',
      type: 'qr',
      identifier,
      metadata: { legacy: true },
    })
  );
  (item.acrIds || (item.acrId ? [item.acrId] : [])).forEach(
    (identifier, index) =>
      legacyAnchors.push({
        id: `legacy-audio-${index}-${identifier}`,
        name: 'Recognized audio',
        type: 'audio',
        identifier,
        metadata: { legacy: true },
      })
  );
  if (item.location?.latitude != null && item.location?.longitude != null) {
    legacyAnchors.push({
      id: `legacy-location-${item.objectId}`,
      name: 'Location',
      type: item.geoFence ? 'geo-fence' : 'geo-point',
      coordinates: {
        latitude: item.location.latitude,
        longitude: item.location.longitude,
      },
      metadata: { legacy: true },
    });
  }
  return {
    ...item,
    id: item.id || item.objectId,
    name: item.name || contentTitle(item.content),
    content: item.content || [],
    audience: {
      visibility:
        item.audience?.visibility || (item.isPublic ? 'public' : 'private'),
      identifiers: item.audience?.identifiers || [],
      ...(item.audience || {}),
    },
    lifecycle: {
      status: item.lifecycle?.status || item.state || 'active',
      startAt: item.lifecycle?.startAt || item.startDate || null,
      endAt: item.lifecycle?.endAt || item.endDate || null,
      recurrence: item.lifecycle?.recurrence || 'none',
      recurrenceWindow: item.lifecycle?.recurrenceWindow || null,
      ...(item.lifecycle || {}),
    },
    experiences: item.experiences || [],
    tags: item.tags || [],
    metadata: item.metadata || {},
    anchors: item.anchors || legacyAnchors,
  };
};

const normalizeList = (response) => {
  const value = normalize(response);
  const items = Array.isArray(value)
    ? value
    : value?.results || value?.objects || value?.items || [];
  return items.map(normalizeLifeObject);
};

export const normalizePeople = (response) => {
  const value = normalize(response);
  const items = Array.isArray(value)
    ? value
    : value?.followers || value?.results || [];
  return items
    .map((item) => ({
      id: item.id || item.objectId,
      name: item.name || item.displayName || null,
      email: item.email || null,
    }))
    .filter((item) => item.id);
};

export const normalizeChannels = (response) => {
  const value = normalize(response);
  const items = Array.isArray(value)
    ? value
    : value?.channels || value?.results || [];
  return items
    .map((item) => ({
      id: item.id || item.objectId || item.name,
      name: item.name || item.label || item.id,
    }))
    .filter((item) => item.id);
};

export const normalizeContextResponse = (response) => {
  const value = normalize(response) || {};
  return {
    ...value,
    objects: normalizeList(value.objects || []),
    totalMatched: value.totalMatched ?? 0,
    totalVisible: value.totalVisible ?? 0,
    contextSnapshot: value.contextSnapshot || {},
  };
};

export const toContractInput = (input) => {
  const anchors = input.anchors || [];
  const beaconIds = anchors
    .filter((anchor) => ['qr', 'ble'].includes(anchor.type))
    .map((anchor) => anchor.identifier)
    .filter(Boolean);
  const audioIds = anchors
    .filter((anchor) => anchor.type === 'audio')
    .map((anchor) => anchor.identifier)
    .filter(Boolean);
  const location = anchors.find((anchor) =>
    ['geo-point', 'geo-fence'].includes(anchor.type)
  );
  return {
    ...input,
    isPublic: input.audience?.visibility === 'public',
    beaconId: beaconIds,
    acrIds: audioIds,
    ...(audioIds[0] ? { acrId: audioIds[0] } : {}),
    ...(location?.coordinates
      ? {
          latitude: location.coordinates.latitude,
          longitude: location.coordinates.longitude,
          geoFence: location.type === 'geo-fence',
        }
      : {}),
    ...(input.lifecycle?.startAt ? { startDate: input.lifecycle.startAt } : {}),
    ...(input.lifecycle?.endAt ? { endDate: input.lifecycle.endAt } : {}),
    recurrence: input.lifecycle?.recurrence,
    state: input.lifecycle?.status,
    content: input.content
      ?.filter((item) => item.body || item.url || item.assetId)
      .map((item) => {
        const contractItem = { ...item };
        delete contractItem.assetPreviewUrl;
        return contractItem;
      }),
  };
};

const run = async (functionName, params = {}) => {
  // Parse.Cloud.run automatically sends the existing Parse session token.
  // Authentication ownership remains with Parse; this client does not store or replace it.
  try {
    const result = await Parse.Cloud.run(functionName, params);
    return normalize(result);
  } catch (error) {
    throw friendlyError(error);
  }
};

const mockApi = {
  async listBrowserSubscribers() {
    return {
      subscriberCount: 2,
      items: [
        { id: 'mock-follower-1', name: 'Erica' },
        { id: 'mock-follower-2', name: 'Browser subscriber' },
      ],
    };
  },
  async createBluetoothBeacon(input) {
    return { ...input, id: `mock-bluetooth-${Date.now()}` };
  },
  async listBluetoothBeacons() {
    return [];
  },
  async createLifeCode(input) {
    return { ...input, id: `mock-lifecode-${Date.now()}` };
  },
  async listLifeCodes() {
    return [];
  },
  async listFollowers() {
    return [
      { id: 'mock-follower-1', name: 'Erica', email: 'erica@example.com' },
      { id: 'mock-follower-2', name: 'Jordan', email: 'jordan@example.com' },
    ];
  },
  async listAudienceChannels() {
    return [{ id: 'campus-updates', name: 'Campus updates' }];
  },
  async recordPerception(input) {
    return { id: `mock-perception-${Date.now()}`, ...input };
  },
  async uploadAsset(file) {
    return {
      id: `mock-asset-${Date.now()}`,
      name: file.name,
      url: URL.createObjectURL(file),
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    };
  },
  async listLifeObjects({ search = '' } = {}) {
    const needle = search.trim().toLowerCase();
    return mockLifeObjects.filter((item) =>
      needle ? item.name.toLowerCase().includes(needle) : true
    );
  },
  async listLifeObjectsPage({ page = 1, limit = 100, search = '' } = {}) {
    const items = await this.listLifeObjects({ search });
    const start = (page - 1) * limit;
    return {
      items: items.slice(start, start + limit),
      page,
      limit,
      hasMore: start + limit < items.length,
      total: items.length,
    };
  },
  async getLifeObject(id) {
    const item = mockLifeObjects.find((candidate) => candidate.id === id);
    if (!item) throw new Error('LifeObject not found');
    return item;
  },
  async createLifeObject(input) {
    const created = {
      ...mockLifeObjects[0],
      ...input,
      id: `mock-${Date.now()}`,
    };
    mockLifeObjects.unshift(created);
    return created;
  },
  async updateLifeObject(id, input) {
    const index = mockLifeObjects.findIndex((item) => item.id === id);
    if (index < 0) throw new Error('LifeObject not found');
    mockLifeObjects[index] = { ...mockLifeObjects[index], ...input, id };
    return mockLifeObjects[index];
  },
  async resolveContext(input) {
    const identifiers = input.anchors.map((anchor) => anchor.identifier);
    const objects = mockLifeObjects.filter((item) =>
      item.anchors.some((anchor) => identifiers.includes(anchor.identifier))
    );
    return {
      objects,
      totalMatched: objects.length,
      totalVisible: objects.length,
      contextSnapshot: {
        resolvedAnchorIds: objects.flatMap((item) =>
          item.resolvedVia.map((entry) => entry.anchorId)
        ),
        geoQueryPerformed: Boolean(input.location),
        capabilities: input.capabilities,
        evaluatedAt: new Date().toISOString(),
      },
    };
  },
  async resolveSignal() {
    return { resolved: false, reason: 'mock_no_match' };
  },
  async listAnchors() {
    return mockLifeObjects.flatMap((item) => item.anchors || []);
  },
  async createAnchor(input) {
    return { ...input, id: `mock-anchor-${Date.now()}` };
  },
  async linkAnchor(lifeObjectId, anchorId) {
    return { lifeObjectId, anchorId };
  },
  async unlinkAnchor(lifeObjectId, anchorId) {
    return { lifeObjectId, anchorId };
  },
  async getInvitationPreview() {
    return {
      state: 'ready',
      headline: 'Someone left something for you',
      canOpenApp: true,
    };
  },
  async claimInvitation() {
    return { claimed: true, appPath: '/moments/mock-moment' };
  },
};

const parseApi = {
  createBluetoothBeacon: (input) =>
    run('createBeacon', {
      ...input,
      apiKey: process.env.REACT_APP_TAGGER_API_KEY,
    }),
  listBluetoothBeacons: (params = {}) =>
    run('getDashboardBlueToothBeacons', params),
  createLifeCode: (input) =>
    run('createBeacon', {
      ...input,
      apiKey: process.env.REACT_APP_TAGGER_API_KEY,
    }),
  listLifeCodes: (params = {}) => run('getLifeCodes', params),
  listFollowers: async () => normalizePeople(await run('getFollowers')),
  listBrowserSubscribers: async () => run('listBrowserSubscribers'),
  listAudienceChannels: async () =>
    normalizeChannels(await run('getUserChannels')),
  recordPerception: (input) => run('recordPerception', input),
  async uploadAsset(file) {
    const response = await run('getUploadUrl', {
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    });
    const uploadUrl = response.uploadUrl || response.signedUrl || response.url;
    if (!uploadUrl) throw new Error('The upload service did not return a URL.');
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!uploadResponse.ok)
      throw new Error('The media upload did not complete.');
    const asset = response.asset || response;
    return {
      id: asset.id || asset.objectId || response.assetId,
      name: asset.name || file.name,
      url: asset.publicUrl || asset.assetUrl || null,
      mimeType: asset.mimeType || file.type || 'application/octet-stream',
      sizeBytes: asset.sizeBytes || file.size,
    };
  },
  listLifeObjects: async (params = {}) => {
    const items = normalizeList(
      await run('getMyLifeObjects', {
        ...params,
        limit: params.limit || 100,
        includeArchived: params.includeArchived ?? true,
      })
    );
    const needle = params.search?.trim().toLowerCase();
    return items.filter((item) =>
      needle ? item.name?.toLowerCase().includes(needle) : true
    );
  },
  async listLifeObjectsPage(params = {}) {
    const page = params.page || 1;
    const limit = Math.min(params.limit || 100, 100);
    const response = await run('getMyLifeObjects', {
      ...params,
      page,
      limit,
      includeArchived: params.includeArchived ?? true,
      includeTotal: true,
    });
    const items = normalizeList(response);
    const needle = params.search?.trim().toLowerCase();
    const filtered = items.filter((item) =>
      needle ? item.name?.toLowerCase().includes(needle) : true
    );
    return {
      items: filtered,
      page,
      limit,
      hasMore: response?.hasMore ?? items.length === limit,
      total: needle ? null : response?.total ?? null,
    };
  },
  getLifeObject: async (id) =>
    normalizeLifeObject(await run('getLifeObject', { id })),
  createLifeObject: async (input) =>
    normalizeLifeObject(await run('createLifeObject', toContractInput(input))),
  updateLifeObject: async (id, input) =>
    normalizeLifeObject(
      await run('updateLifeObject', { id, ...toContractInput(input) })
    ),
  deleteLifeObject: (id) => run('deleteLifeObject', { id }),
  resolveContext: async (input) =>
    normalizeContextResponse(await run('resolveContext', input)),
  resolvePushExperience: async (input) =>
    normalizeContextResponse(await run('resolvePushExperience', input)),
  resolveSignal: (input) => run('resolveSignal', input),
  listAnchors: (params = {}) => run('getAnchors', params),
  createAnchor: (input) => run('createAnchor', input),
  linkAnchor: (lifeObjectId, anchorId) =>
    run('linkAnchor', { lifeObjectId, anchorId }),
  unlinkAnchor: (lifeObjectId, anchorId) =>
    run('unlinkAnchor', { lifeObjectId, anchorId }),
  getInvitationPreview: (token) =>
    run('getLifeObjectInvitationPreview', { token }),
  claimInvitation: (token) => run('claimLifeObjectInvitation', { token }),
};

export const lifeTaggerApi = useMocks() ? mockApi : parseApi;
export const activeTransport = useMocks() ? 'mock' : 'parse';

export const createLifeCodeSignal = async (input) => {
  const identifier = String(input.beaconId || '').toUpperCase();
  const existingLifeCodes = await lifeTaggerApi.listLifeCodes({ limit: 1000 });
  let lifeCode = (existingLifeCodes || []).find(
    (item) => String(item.beaconId || '').toUpperCase() === identifier
  );
  if (!lifeCode) lifeCode = await lifeTaggerApi.createLifeCode(input);

  const existingAnchors = await lifeTaggerApi.listAnchors({ limit: 1000 });
  let anchor = (existingAnchors || []).find(
    (item) =>
      item.type === 'qr' &&
      String(item.identifier || '').toUpperCase() === identifier
  );
  if (!anchor) {
    anchor = await lifeTaggerApi.createAnchor({
      type: 'qr',
      name: input.name,
      identifier: input.beaconId,
      metadata: {
        description: input.description,
        lifeCodeId: lifeCode?.id || lifeCode?.objectId || null,
      },
    });
  }
  return { lifeCode, anchor };
};

export const normalizeBluetoothUuid = (value) => {
  const compact = String(value || '')
    .trim()
    .replace(/[{}\s-]/g, '')
    .toUpperCase();
  if (!/^[0-9A-F]{32}$/.test(compact)) return null;
  return [8, 12, 16, 20]
    .reduce(
      (parts, boundary) => {
        const prior = parts.boundary;
        parts.values.push(compact.slice(prior, boundary));
        parts.boundary = boundary;
        return parts;
      },
      { values: [], boundary: 0 }
    )
    .values.concat(compact.slice(20))
    .join('-');
};

export const createBluetoothSignal = async (input) => {
  const identifier = normalizeBluetoothUuid(input.beaconId);
  if (!identifier)
    throw new LifeTaggerApiError('Enter the complete UUID from the beacon.', {
      code: 'invalid_bluetooth_uuid',
    });

  const existingBeacons = await lifeTaggerApi.listBluetoothBeacons({
    limit: 1000,
  });
  let beacon = (existingBeacons || []).find(
    (item) => normalizeBluetoothUuid(item.beaconId) === identifier
  );
  if (!beacon) {
    beacon = await lifeTaggerApi.createBluetoothBeacon({
      ...input,
      beaconId: identifier,
      type: 'beacon',
    });
  }

  const existingAnchors = await lifeTaggerApi.listAnchors({ limit: 1000 });
  let anchor = (existingAnchors || []).find(
    (item) =>
      item.type === 'ble' &&
      normalizeBluetoothUuid(item.identifier) === identifier
  );
  if (!anchor) {
    anchor = await lifeTaggerApi.createAnchor({
      type: 'ble',
      name: input.name,
      identifier,
      metadata: {
        description: input.description,
        beaconId: beacon?.id || beacon?.objectId || null,
      },
    });
  }
  return { beacon, anchor };
};

export const normalizeWebAddress = (value, { includeQuery = false } = {}) => {
  try {
    const url = new URL(String(value || '').trim());
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    url.hash = '';
    if (!includeQuery) url.search = '';
    url.hostname = url.hostname.toLowerCase();
    if (url.pathname !== '/') url.pathname = url.pathname.replace(/\/$/, '');
    return url.toString();
  } catch (_) {
    return null;
  }
};

export const createWebAddressSignal = async (input) => {
  const identifier = normalizeWebAddress(input.url, {
    includeQuery: Boolean(input.includeQuery),
  });
  if (!identifier)
    throw new LifeTaggerApiError(
      'Enter a complete web address beginning with http:// or https://.',
      { code: 'invalid_web_address' }
    );
  const existingAnchors = await lifeTaggerApi.listAnchors({ limit: 1000 });
  const existing = (existingAnchors || []).find(
    (item) =>
      item.type === 'url' &&
      normalizeWebAddress(item.identifier, {
        includeQuery: Boolean(item.metadata?.includeQuery),
      }) === identifier
  );
  if (existing) return { anchor: existing, existing: true };
  const anchor = await lifeTaggerApi.createAnchor({
    type: 'url',
    name: input.name,
    identifier,
    metadata: {
      description: input.description,
      includeQuery: Boolean(input.includeQuery),
    },
  });
  return { anchor, existing: false };
};

const anchorSignature = (anchor) => {
  if (anchor.identifier) return `${anchor.type}:id:${anchor.identifier}`;
  if (anchor.coordinates) {
    return `${anchor.type}:geo:${Number(anchor.coordinates.latitude).toFixed(
      6
    )},${Number(anchor.coordinates.longitude).toFixed(6)}:${
      anchor.radiusMeters || ''
    }`;
  }
  return `${anchor.type}:unknown`;
};

const persistedAnchorId = (anchor) =>
  anchor.id &&
  !String(anchor.id).startsWith('draft-') &&
  !String(anchor.id).startsWith('legacy-')
    ? anchor.id
    : null;

export const syncLifeObjectAnchors = async ({
  lifeObjectId,
  previous = [],
  next = [],
}) => {
  if (!apiCapabilities.anchorPersistence || !lifeObjectId) return next;

  const catalog = await lifeTaggerApi.listAnchors({ limit: 100 });
  const catalogBySignature = new Map(
    catalog.map((anchor) => [anchorSignature(anchor), anchor])
  );
  const resolvedNext = [];

  for (const requested of next) {
    let anchor = persistedAnchorId(requested)
      ? requested
      : catalogBySignature.get(anchorSignature(requested));
    if (!anchor) {
      const input = { ...requested };
      delete input.id;
      anchor = await lifeTaggerApi.createAnchor(input);
      catalogBySignature.set(anchorSignature(anchor), anchor);
    }
    await lifeTaggerApi.linkAnchor(lifeObjectId, anchor.id);
    resolvedNext.push(anchor);
  }

  const desiredIds = new Set(resolvedNext.map((anchor) => anchor.id));
  for (const prior of previous) {
    const priorAnchor = persistedAnchorId(prior)
      ? prior
      : catalogBySignature.get(anchorSignature(prior));
    if (priorAnchor?.id && !desiredIds.has(priorAnchor.id)) {
      await lifeTaggerApi.unlinkAnchor(lifeObjectId, priorAnchor.id);
    }
  }

  return resolvedNext;
};

export const apiCapabilities = {
  managedAssetUpload: true,
  anchorPersistence: true,
  contextResolution: true,
  perceptionRecording: true,
  supportedAnchorTypes: [
    'qr',
    'geo-point',
    'geo-fence',
    'nfc',
    'ble',
    'audio',
    'url',
    'connector',
  ],
};
